import React, { useCallback, useMemo, useState } from 'react';
import { uniq } from 'lodash/fp';

import { DocumentNamePageView, fetchAllAgreementTypeRelationshipsStarted, fetchAllAgreementTypesStarted, getAgreementTypeRelationships, getCurrentAgreementTypes, setDocumentNamePageView } from './store';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { Button } from '../../shared/button/Button';
import { AgreementFamilyTreeChart, TreeData } from './AllAgreementsFamilyTreeChart';
import { useWindowResize } from '../../../hooks/useWindowResize';
import styles from './DocumentName.module.scss';
import { CustomTooltip } from '../../shared/tooltip';
import { IconButton } from '../../shared/button/IconButton';
import { Add, Minus, Repeat } from '../../shared/icons';

const NAV_BAR = 100;
const BUTTONS = 50;
const TITLE = 40;
const MARGINS = 80;
const HEIGHT_OFFSET = NAV_BAR + BUTTONS + TITLE + MARGINS;

const { french } = styles;

export const AllAgreementsFamilyTree: React.FC = () => {

    const [screenWidth, screenHeight] = useWindowResize();
    const [resetZoom, setResetZoom] = useState<boolean>(false);
    const [zoomSteps, setZoomSteps] = useState<number>(0);
    const [zoomUpdated, setZoomUpdated] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    useFetchStarted([fetchAllAgreementTypeRelationshipsStarted(), fetchAllAgreementTypesStarted()]);

    const allRelationships = useAppSelector(getAgreementTypeRelationships);
    const allAgreementTypes = useAppSelector(getCurrentAgreementTypes);

    const allParentIds = useMemo(() => allRelationships.map(({ parentAgreementTypeId }) => parentAgreementTypeId), [allRelationships]);
    const allChildIds = useMemo(() => allRelationships.map(({ childAgreementTypeId }) => childAgreementTypeId), [allRelationships]);

    const ultimateParentIds = useMemo(() => uniq(allParentIds.filter(id => !allChildIds.includes(id))), [allParentIds, allChildIds]);

    const backToAgreementSelect = useCallback(() => dispatch(setDocumentNamePageView(DocumentNamePageView.RELATIONSHIPS)), [dispatch]);

    const getAgreementTypeNameById = useCallback((agreementTypeId: number) => allAgreementTypes.find(agreement => agreement.agreementTypeId === agreementTypeId)?.name || '', [allAgreementTypes]);

    const data = allRelationships.map(({ parentAgreementTypeId, childAgreementTypeId }) => ({ name: getAgreementTypeNameById(childAgreementTypeId), agreementTypeId: childAgreementTypeId, parent: getAgreementTypeNameById(parentAgreementTypeId), parentAgreementTypeId, children: [] }));

    const getChildren = useCallback((id: number, treeData: TreeData[]): TreeData[] => {
        const childrenForAgreementTypeId = data.filter(({ parentAgreementTypeId }) => parentAgreementTypeId === id);
        return childrenForAgreementTypeId.map(childAgreement => ({ ...childAgreement, children: getChildren(childAgreement.agreementTypeId, treeData) }));
    }, [data]);

    const treeData = useMemo(() => {
        let treeData: TreeData[] = [{ name: 'Ark-51', parent: null, agreementTypeId: 0, parentAgreementTypeId: null, children: [] }];
        ultimateParentIds.map(id => treeData[0].children!.push({ name: getAgreementTypeNameById(id), parent: 'Ark-51', agreementTypeId: id, parentAgreementTypeId: 0, children: getChildren(id, treeData) }));
        return treeData;
    }, [getAgreementTypeNameById, ultimateParentIds, getChildren]);

    const resetMapZoom = () => {
        setResetZoom(!resetZoom);
        setZoomSteps(0);
        setZoomUpdated(false);
    };

    const mapZoomIn = useCallback(() => {
        setResetZoom(false);
        zoomSteps < 0 ? setZoomSteps(1) : setZoomSteps(zoomSteps + 1);
        setZoomUpdated(true);
    }, [zoomSteps]);

    const mapZoomOut = useCallback(() => {
        setResetZoom(false);
        zoomSteps > 0 ? setZoomSteps(-1) : setZoomSteps(zoomSteps - 1);
        setZoomUpdated(true);
    }, [zoomSteps]);

    return (
        <div className={styles.agreementFamilyWrapper}>
            <div className={styles.titleWrapper}>
                <div className={styles.title}>Agreement Family Tree</div>
            </div>
            <div className={styles.agreementFamilyChart} style={{ width: `${screenWidth * 0.9}px` }}>
                <AgreementFamilyTreeChart
                    treeData={treeData}
                    height={screenHeight - HEIGHT_OFFSET}
                    width={screenWidth * 0.9}
                    resetZoom={resetZoom}
                    zoomSteps={zoomSteps}
                    zoomUpdated={zoomUpdated}
                />
                <div className={styles.familyChartButtons}>
                    <Button label='Back' onClick={backToAgreementSelect} />
                    <div className={styles.chartButtons}>
                        <CustomTooltip overlayText='Zoom In' placement='top'>
                            <div>
                                <IconButton onClick={mapZoomIn} testId='jurisdiction-map-zoom-in' icon={Add} fontSize={34} withBackground />
                            </div>
                        </CustomTooltip>
                        <CustomTooltip overlayText='Reset Zoom' placement='top'>
                            <div>
                                <IconButton onClick={resetMapZoom} disabled={false} testId='jurisdiction-map-reset-zoom' icon={Repeat} fontSize={34} withBackground iconFill={french} />
                            </div>
                        </CustomTooltip>
                        <CustomTooltip overlayText='Zoom Out' placement='top'>
                            <div>
                                <IconButton onClick={mapZoomOut} testId='jurisdiction-map-zoom-out' icon={Minus} fontSize={34} withBackground />
                            </div>
                        </CustomTooltip>
                    </div>
                </div>
            </div>
        </div>
    );
};
